import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard } from '../ui/GlassComponents';
import RemapAdjustmentsModal from './RemapAdjustmentsModal';
import { useConfirm } from '../ui/ConfirmDialog';

/**
 * Admin-only panel for assigning a team member to a department + one or
 * more leave policies. Triggers the leave-adjustment remap flow when
 * assigning a policy to a user who has existing +/- adjustments.
 *
 * Props:
 *   userId — the team member being edited.
 */
const AssignmentsTab = ({ userId }) => {
  const confirm = useConfirm();
  const [departments, setDepartments] = useState([]);
  const [allPolicies, setAllPolicies] = useState([]);
  const [assignedPolicies, setAssignedPolicies] = useState([]);
  const [currentDepartmentId, setCurrentDepartmentId] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingDept, setSavingDept] = useState(false);

  const [remapOpen, setRemapOpen] = useState(false);
  const [pendingPolicyId, setPendingPolicyId] = useState(null);
  const [remapProposal, setRemapProposal] = useState(null);
  const [remapTargetTypes, setRemapTargetTypes] = useState([]);

  const loadAll = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [deps, pols, assigned, memberResp] = await Promise.all([
        axios.get('/api/departments').catch(() => ({ data: { departments: [] } })),
        axios.get('/api/leave-policies').catch(() => ({ data: { policies: [] } })),
        axios.get(`/api/users/${userId}/policies`).catch(() => ({ data: { policies: [] } })),
        axios.get(`/api/users/${userId}`).catch(() => null)
      ]);
      setDepartments(deps.data.departments || []);
      setAllPolicies(pols.data.policies || []);
      setAssignedPolicies(assigned.data.policies || []);
      const member = memberResp?.data?.user;
      setCurrentDepartmentId(member?.departmentId || '');
    } catch (e) {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [userId]);

  const saveDepartment = async (newId) => {
    setSavingDept(true);
    try {
      await axios.put(`/api/users/${userId}`, { departmentId: newId || null });
      setCurrentDepartmentId(newId || '');
      toast.success('Department updated');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update department');
    } finally {
      setSavingDept(false);
    }
  };

  const unassignPolicy = async (policyId) => {
    const ok = await confirm({
      title: 'Remove this policy from the user?',
      confirmLabel: 'Remove',
      destructive: true
    });
    if (!ok) return;
    try {
      await axios.delete(`/api/users/${userId}/policies/${policyId}`);
      setAssignedPolicies(prev => prev.filter(p => p.id !== policyId));
      toast.success('Policy removed');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to remove policy');
    }
  };

  const startAssignPolicy = async (policyId) => {
    if (!policyId) return;
    setPendingPolicyId(policyId);
    try {
      const r = await axios.get(`/api/users/${userId}/policies`, {
        params: { candidatePolicyId: policyId }
      });
      const proposal = r.data.remapProposal;
      if (proposal && proposal.hasAdjustments) {
        const target = allPolicies.find(p => p.id === policyId);
        const targetTypes = target?.leave_types || target?.leaveTypes || [];
        setRemapTargetTypes(targetTypes);
        setRemapProposal(proposal);
        setRemapOpen(true);
      } else {
        await axios.post(`/api/users/${userId}/policies`, { policyId });
        const pol = allPolicies.find(p => p.id === policyId);
        if (pol) setAssignedPolicies(prev => [...prev, pol]);
        toast.success('Policy assigned');
        setPendingPolicyId(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to assign policy');
      setPendingPolicyId(null);
    }
  };

  const confirmRemap = async (adjustmentActions) => {
    if (!pendingPolicyId) return;
    try {
      await axios.post(`/api/users/${userId}/policies`, {
        policyId: pendingPolicyId,
        adjustmentActions
      });
      const pol = allPolicies.find(p => p.id === pendingPolicyId);
      if (pol) setAssignedPolicies(prev => [...prev, pol]);
      toast.success('Policy assigned');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to assign policy');
    } finally {
      setRemapOpen(false);
      setRemapProposal(null);
      setPendingPolicyId(null);
    }
  };

  const cancelRemap = () => {
    setRemapOpen(false);
    setRemapProposal(null);
    setPendingPolicyId(null);
  };

  const unassignedPolicies = allPolicies.filter(
    p => !assignedPolicies.some(a => a.id === p.id)
  );

  if (loading) {
    return (
      <GlassCard className="p-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      </GlassCard>
    );
  }

  return (
    <>
      <GlassCard className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Assignments</h3>

        {/* Department */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Department
          </label>
          <div className="flex items-center space-x-3">
            <select
              value={currentDepartmentId || ''}
              onChange={e => saveDepartment(e.target.value)}
              disabled={savingDept}
              className="block w-full md:w-1/2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm disabled:opacity-50"
            >
              <option value="">No department</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            {savingDept && <span className="text-xs text-gray-500">Saving…</span>}
          </div>
          {departments.length === 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              No departments configured yet. Create one in Team Settings → Departments.
            </p>
          )}
        </div>

        {/* Policies */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Leave Policies
          </label>
          {assignedPolicies.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              None assigned — this user falls back to your team&apos;s default leave types.
            </p>
          ) : (
            <ul className="space-y-2 mb-3">
              {assignedPolicies.map(p => (
                <li key={p.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{p.name}</span>
                    {p.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{p.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => unassignPolicy(p.id)}
                    className="text-sm text-red-600 hover:text-red-900 dark:text-red-400"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}

          {unassignedPolicies.length > 0 ? (
            <select
              value=""
              onChange={e => {
                const v = e.target.value;
                e.target.value = '';
                if (v) startAssignPolicy(v);
              }}
              className="block w-full md:w-1/2 text-sm rounded-md border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="">+ Assign policy…</option>
              {unassignedPolicies.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          ) : allPolicies.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              No policies configured yet. Create one in Team Settings → Policies.
            </p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              This user has every policy assigned.
            </p>
          )}
        </div>
      </GlassCard>

      <RemapAdjustmentsModal
        open={remapOpen}
        proposal={remapProposal}
        availableLeaveTypes={remapTargetTypes}
        onCancel={cancelRemap}
        onConfirm={confirmRemap}
      />
    </>
  );
};

export default AssignmentsTab;
